import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HttpService } from '../http.service';
import { Observable } from 'rxjs';
import {
  CreateCancellationCreditNotePayload,
  CreateCreditNotePayload,
  CreateCreditNoteResponse,
  CreditNoteCancellationSource,
  CreditNotePdfLink,
  CreditNoteReturnSource,
  CreditNoteSource
} from '../../../models/admin/credit-note.model';

type CreditNotePayload = CreateCreditNotePayload | CreateCancellationCreditNotePayload;

@Injectable({
  providedIn: 'root'
})
export class CreditNoteService extends HttpService<CreditNotePayload, CreditNoteSource> {
  constructor(httpClient: HttpClient, @Inject('API_URL') apiUrl: string, @Inject('API_VERSION') apiVersion: string) {
    super(httpClient, apiUrl, apiVersion, 'credit-note');
  }

  /** Load a return's refund-approved, un-credited items. `creditNoteId` is the return id. */
  getCreditNote(creditNoteId: number): Observable<CreditNoteReturnSource> {
    return this.getBy(`?id=${creditNoteId}`) as Observable<CreditNoteReturnSource>;
  }

  createCreditNote(creditNote: CreateCreditNotePayload): Observable<CreateCreditNoteResponse> {
    return this.create(creditNote) as Observable<CreateCreditNoteResponse>;
  }

  /** Load a confirmed cancelled order's items for a whole-order credit note. */
  getCancellation(cancellationId: number): Observable<CreditNoteCancellationSource> {
    return this.getBy(`cancellation?id=${cancellationId}`) as Observable<CreditNoteCancellationSource>;
  }

  /**
   * Resolve a downloadable link for a credit note's PDF.
   *
   * The stored `pdf_url` points at a PRIVATE S3 bucket, so opening it directly gives
   * `AccessDenied`. This endpoint mints a short-lived presigned URL instead, and when
   * the PDF has not been generated yet it kicks generation off and reports that, so
   * the admin can simply retry.
   */
  getPdfDownloadUrl(creditNoteId: number): Observable<CreditNotePdfLink> {
    return this.getBy(`${creditNoteId}/pdf`) as unknown as Observable<CreditNotePdfLink>;
  }

  /** Create a credit note from a confirmed cancellation. */
  createCreditNoteFromCancellation(creditNote: CreateCancellationCreditNotePayload): Observable<CreateCreditNoteResponse> {
    return this.create(creditNote, 'cancellation') as Observable<CreateCreditNoteResponse>;
  }
}
